import {useMutation, useQueryClient} from '@tanstack/react-query'
import {t} from 'i18next'
import {useSnackbar} from 'notistack'
import {v4 as uuidV4} from 'uuid'

import {api} from '../../../../../App.store'
import {CustomerTierSettings} from '../types/CustomerTierSettings'

import {CustomerTierSettingsQueryKey, ProductConfigsQueryKey} from './queryKeys'

const baseUrl = 'product-configs/customerTierSettings'

const deleteCustomerTierSetting = async (id: string) => {
  await api.delete(`${baseUrl}/${id}`)
  return id
}

const createCustomerTierSetting = async (customerTierSetting: CustomerTierSettings) => {
  customerTierSetting.id = uuidV4()
  if (!customerTierSetting.dispatchGroup || customerTierSetting.dispatchGroup.trim().length === 0) {
    customerTierSetting.dispatchGroup = null
  }
  const response = await api.post<CustomerTierSettings>(baseUrl, customerTierSetting)
  const {data: createdCustomerTierSetting} = response
  return createdCustomerTierSetting
}

const editCustomerTierSetting = async (customerTierSetting: CustomerTierSettings) => {
  await api.put<CustomerTierSettings>(`${baseUrl}/${customerTierSetting.id}`, customerTierSetting)
  return customerTierSetting
}
type MutationType = 'create' | 'edit' | 'delete'

export const useCustomerTierSettingCreateMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    createCustomerTierSetting,
    getMutationHandlers(queryClient, enqueueSnackbar, 'create')
  )
}
export const useCustomerTierSettingEditMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    editCustomerTierSetting,
    getMutationHandlers(queryClient, enqueueSnackbar, 'edit')
  )
}
export const useCustomerTierSettingDeleteMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    deleteCustomerTierSetting,
    getMutationHandlers(queryClient, enqueueSnackbar, 'delete')
  )
}

const getMutationHandlers = (queryClient, enqueueSnackbar, mutationType: MutationType) => ({
  onSuccess: () => {
    const successMessageKey = {
      create: 'configurations.hconnect.customerTierSettings.configurationList.createSuccess',
      edit: 'configurations.hconnect.customerTierSettings.configurationList.editSuccess',
      delete: 'configurations.hconnect.customerTierSettings.configurationList.deleteSuccess'
    }[mutationType]

    enqueueSnackbar(t(successMessageKey), {variant: 'success'})
    queryClient.invalidateQueries([ProductConfigsQueryKey, CustomerTierSettingsQueryKey])
  },
  onError: (error: any) => {
    const errorMessageKey = {
      create: 'configurations.hconnect.customerTierSettings.configurationList.createError',
      edit: 'configurations.hconnect.customerTierSettings.configurationList.editError',
      delete: 'configurations.hconnect.customerTierSettings.configurationList.deleteError'
    }[mutationType]

    enqueueSnackbar(t(errorMessageKey), {variant: 'error'})
    console.error('Mutation error:', error)
  }
})
